/**
 * Dependency imports
 */
import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

/**
 * Local imports
 */
import TransitionLink from './TransitionLink';

/**
 * Root
 */
const Root = styled('div', {
  shouldForwardProp: (prop: PropertyKey) =>
    !['hasArrow', 'withOpacity'].includes(prop.toString()),
})<Partial<UnderlineLinkCombinedProps>>(
  ({ hasArrow = false, withOpacity = false }) => css`
    position: relative;
    z-index: 0;

    > a {
      display: inline-flex;
      align-items: center;
      position: relative;
      padding: 1rem 1.25rem;

      > span {
        position: absolute;
        top: 70%;
        left: 50%;
        width: 50%;
        height: 3px;
        z-index: -1;
        opacity: 0;
        text-align: center;
        transform: translate(-50%, -50%);
        transition: 300ms transform cubic-bezier(0.27, 0.34, 0.56, 2.1) 50ms,
          140ms opacity 50ms;

        > span {
          display: block;
          height: 100%;
          background-color: var(--color-primary);
          border-radius: 999px;
          transform: scaleX(0);
          transform-origin: 100% center;
          transition: 140ms transform ease-in-out;
        }
      }

      &:hover {
        > span {
          opacity: 1;
          transition: 100ms transform, 140ms opacity;

          > span {
            transform-origin: 0% center;
            transform: scaleX(1);
          }
        }
      }

      ${hasArrow &&
      css`
        &:after {
          content: '';
          display: block;
          width: 0.4rem;
          height: 0.4rem;
          margin-inline-start: 0.5rem;
          border-top: 1.5px solid;
          border-inline-end: 1.5px solid;
          transform: rotate(45deg);
        }
      `}

      ${withOpacity &&
      css`
        opacity: 0.7;
        transition: 200ms opacity ease-in-out;

        &:hover {
          opacity: 1;
        }
      `}
    }
  `
);

/**
 * UnderlineLink Component
 */
export interface UnderlineLinkProps {
  href: string;
  hasArrow?: boolean;
  transition?: boolean;
  withOpacity?: boolean;
}

export type UnderlineLinkCombinedProps = UnderlineLinkProps &
  JSX.IntrinsicElements['div'];

const UnderlineLink: React.FC<UnderlineLinkCombinedProps> = ({
  href,
  transition = true,
  children,
  ...props
}) => {
  const spanRef = React.useRef<HTMLSpanElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const elementWidth = e.currentTarget.offsetWidth;
    const elementHeight = e.currentTarget.offsetHeight;
    const offsetX = e.nativeEvent.offsetX - 0.5 * elementWidth;
    const offsetY = e.nativeEvent.offsetY - 0.25 * elementHeight;
    const translateX = `calc(-50% + ${(50 * offsetX) / elementWidth}%)`;
    const translateY = `calc(-50% + ${(250 * offsetY) / elementHeight}%)`;

    if (spanRef.current) {
      spanRef.current.style.transform = `translate(${translateX}, ${translateY})`;
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (spanRef.current) {
      spanRef.current.style.transform = '';
    }
  };

  return (
    <Root
      {...props}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <TransitionLink href={href} transition={transition}>
        {children}
        <span ref={spanRef}>
          <span />
        </span>
      </TransitionLink>
    </Root>
  );
};

export default UnderlineLink;
